import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AlertModal from '../components/AlertModal';
import PremiumSelect from '../components/PremiumSelect';
import { User, Cycle, AlertState } from '../types';
import './CreateFeedback.css';

const yesNoPartialOptions = [
  { value: 'sim', label: 'Sim' },
  { value: 'parcialmente', label: 'Parcialmente' },
  { value: 'não', label: 'Não' },
];

interface FeedbackFormData {
  developerId: string;
  cycleId: string;
  screensDeveloped: number;
  tasksCompleted: number;
  hoursWorked: number;
  averageComplexity: number;
  productivityScore: number;
  productivityJustification: string;
  productivityObservations: string;
  codeQualityScore: number;
  codeAdherence: 'sim' | 'parcialmente' | 'não';
  codeQualityObservations: string;
  validationScore: number;
  validationCompleteness: 'sim' | 'parcialmente' | 'não';
  validationObservations: string;
  strengths: string;
  improvements: string;
  nextActions: string;
}

const CreateFeedback: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [developers, setDevelopers] = useState<User[]>([]);
  const [cycles, setCycles] = useState<Cycle[]>([]);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [formData, setFormData] = useState<FeedbackFormData>({
    developerId: '',
    cycleId: '',
    screensDeveloped: 0,
    tasksCompleted: 0,
    hoursWorked: 0,
    averageComplexity: 0,
    productivityScore: 3,
    productivityJustification: '',
    productivityObservations: '',
    codeQualityScore: 3,
    codeAdherence: 'sim',
    codeQualityObservations: '',
    validationScore: 3,
    validationCompleteness: 'sim',
    validationObservations: '',
    strengths: '',
    improvements: '',
    nextActions: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (): Promise<void> => {
    try {
      const [usersRes, cyclesRes] = await Promise.all([
        api.get<User[]>('/users/team'),
        api.get<Cycle[]>('/cycles'),
      ]);
      setDevelopers(usersRes.data);
      setCycles(cyclesRes.data.filter((c) => c.active));
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  };

  const developerSelectOptions = useMemo(
    () => [
      { value: '', label: 'Selecione...' },
      ...developers.map((dev) => ({
        value: dev.id,
        label: `${dev.name} (${dev.email})`,
      })),
    ],
    [developers],
  );

  const cycleSelectOptions = useMemo(
    () => [
      { value: '', label: 'Selecione...' },
      ...cycles.map((cycle) => ({ value: cycle.id, label: cycle.name })),
    ],
    [cycles],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name.includes('Score') || name.includes('Complexity') || name.includes('Worked') || name.includes('Developed') || name.includes('Completed')
        ? parseFloat(value) || 0
        : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/feedbacks', formData);
      setAlert({
        isOpen: true,
        message: 'Feedback criado com sucesso!',
        type: 'success',
      });
      setTimeout(() => {
        navigate('/feedbacks');
      }, 1500);
    } catch (error: any) {
      setAlert({
        isOpen: true,
        message: error.response?.data?.message || 'Erro ao criar feedback',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <h1>Criar Feedback</h1>
      <form onSubmit={handleSubmit} className="feedback-form">
        <div className="form-section">
          <h2>Informações Básicas</h2>
          <PremiumSelect
            label="Desenvolvedor"
            wrapperClassName="premium-select-field"
            value={formData.developerId}
            options={developerSelectOptions}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                developerId: value,
              }))
            }
            placeholder="Selecione..."
            required
          />
          <PremiumSelect
            label="Ciclo"
            wrapperClassName="premium-select-field"
            value={formData.cycleId}
            options={cycleSelectOptions}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                cycleId: value,
              }))
            }
            placeholder="Selecione..."
            required
          />
        </div>

        <div className="form-section">
          <h2>Produtividade Técnica</h2>
          <div className="form-row">
            <div className="form-group">
              <label>Telas Desenvolvidas</label>
              <input
                type="number"
                name="screensDeveloped"
                value={formData.screensDeveloped}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Tarefas Concluídas</label>
              <input
                type="number"
                name="tasksCompleted"
                value={formData.tasksCompleted}
                onChange={handleChange}
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Horas Trabalhadas</label>
              <input
                type="number"
                name="hoursWorked"
                value={formData.hoursWorked}
                onChange={handleChange}
                min="0"
                step="0.5"
              />
            </div>
            <div className="form-group">
              <label>Complexidade Média (1-10)</label>
              <input
                type="number"
                name="averageComplexity"
                value={formData.averageComplexity}
                onChange={handleChange}
                min="1"
                max="10"
              />
            </div>
          </div>
          <div className="form-group">
            <label>Score de Produtividade (1-5) *</label>
            <input
              type="number"
              name="productivityScore"
              value={formData.productivityScore}
              onChange={handleChange}
              min="1"
              max="5"
              required
            />
          </div>
          <div className="form-group">
            <label>Justificativa *</label>
            <textarea
              name="productivityJustification"
              value={formData.productivityJustification}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Observações</label>
            <textarea
              name="productivityObservations"
              value={formData.productivityObservations}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Qualidade do Código</h2>
          <div className="form-group">
            <label>Score de Qualidade (1-5) *</label>
            <input
              type="number"
              name="codeQualityScore"
              value={formData.codeQualityScore}
              onChange={handleChange}
              min="1"
              max="5"
              required
            />
          </div>
          <PremiumSelect
            label="Aderência aos Padrões"
            wrapperClassName="premium-select-field"
            value={formData.codeAdherence}
            options={yesNoPartialOptions}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                codeAdherence: value as FeedbackFormData['codeAdherence'],
              }))
            }
            required
          />
          <div className="form-group">
            <label>Observações</label>
            <textarea
              name="codeQualityObservations"
              value={formData.codeQualityObservations}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Validação e Testes</h2>
          <div className="form-group">
            <label>Score de Validação (1-5) *</label>
            <input
              type="number"
              name="validationScore"
              value={formData.validationScore}
              onChange={handleChange}
              min="1"
              max="5"
              required
            />
          </div>
          <PremiumSelect
            label="Completude dos Testes"
            wrapperClassName="premium-select-field"
            value={formData.validationCompleteness}
            options={yesNoPartialOptions}
            onChange={(value) =>
              setFormData((prev) => ({
                ...prev,
                validationCompleteness: value as FeedbackFormData['validationCompleteness'],
              }))
            }
            required
          />
          <div className="form-group">
            <label>Observações</label>
            <textarea
              name="validationObservations"
              value={formData.validationObservations}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Consolidado</h2>
          <div className="form-group">
            <label>Pontos Fortes *</label>
            <textarea
              name="strengths"
              value={formData.strengths}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Pontos de Melhoria *</label>
            <textarea
              name="improvements"
              value={formData.improvements}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Ações para o Próximo Ciclo *</label>
            <textarea
              name="nextActions"
              value={formData.nextActions}
              onChange={handleChange}
              required
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/feedbacks')} className="btn btn-secondary">
            Cancelar
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Salvando...' : 'Salvar Feedback'}
          </button>
        </div>
      </form>
      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        message={alert.message}
        type={alert.type}
      />
    </div>
  );
};

export default CreateFeedback;
